import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useApp } from '../context/AppContext';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, parseISO, addMonths, subMonths } from 'date-fns';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiChevronLeft, FiChevronRight, FiX, FiCheck, FiSun, FiMoon, FiRotateCcw } = FiIcons;

const SleepCalendar = ({ childId, showTodayLogging = false, todayLog = null, onUndoLog = null }) => {
  const { sleep_logs, addSleepLog, children } = useApp();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [showModal, setShowModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [modalNote, setModalNote] = useState('');

  const child = children.find(c => c.id === childId);
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const monthDays = eachDayOfInterval({ start: monthStart, end: monthEnd });

  const today = new Date();
  const todayStr = format(today, 'yyyy-MM-dd');

  const getDayLog = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return sleep_logs.find(log => 
      log.childId === childId && log.date === dateStr
    );
  };

  const handleDayClick = (date) => {
    // Don't allow future dates
    if (date > today) return;
    
    setSelectedDate(date);
    const existingLog = getDayLog(date);
    setModalNote(existingLog?.note || '');
    setShowModal(true);
  };

  const handleModalSubmit = (good) => {
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    
    addSleepLog({
      childId,
      date: dateStr,
      good,
      note: modalNote.trim()
    });
    
    setShowModal(false);
    setSelectedDate(null);
    setModalNote('');
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedDate(null);
    setModalNote('');
  };

  const getDayClassName = (date) => {
    const log = getDayLog(date);
    const isToday = isSameDay(date, today);
    const isFuture = date > today;
    
    let baseClasses = "w-12 h-12 flex items-center justify-center text-sm font-medium transition-all duration-200 relative hover:bg-gray-50 ";
    
    if (isFuture) {
      baseClasses += "text-gray-300 cursor-not-allowed ";
    } else if (isToday) {
      baseClasses += "text-gray-800 cursor-pointer ";
    } else if (log) {
      baseClasses += "text-gray-800 cursor-pointer ";
    } else {
      baseClasses += "text-gray-700 cursor-pointer ";
    }
    
    return baseClasses;
  };

  const getDayContent = (date) => {
    const log = getDayLog(date);
    const isToday = isSameDay(date, today);
    const day = format(date, 'd');
    
    return (
      <div className="w-full h-full flex flex-col items-center justify-center relative">
        <span className="text-sm">{day}</span>
        {log && (
          <div className={`absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center ${
            log.good ? 'bg-green-500' : 'bg-red-500'
          }`}>
            <SafeIcon 
              icon={log.good ? FiCheck : FiX} 
              className="text-white text-xs"
            />
          </div>
        )}
        {isToday && (
          <div className="absolute inset-0 border-2 border-blue-500 rounded-full"></div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Today's Logging Section */}
      {showTodayLogging && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">How was bedtime tonight?</h2>
          
          <div className="flex justify-center space-x-4 mb-6">
            <button
              onClick={() => handleDayClick(today)}
              className="flex items-center space-x-3 bg-green-600 text-white px-8 py-4 rounded-2xl hover:bg-green-700 transition-colors font-semibold text-lg shadow-lg hover:shadow-xl"
            >
              <SafeIcon icon={FiSun} className="text-xl" />
              <span>Good Night</span>
            </button>
            
            <button
              onClick={() => handleDayClick(today)}
              className="flex items-center space-x-3 bg-white text-red-600 border-2 border-red-600 px-8 py-4 rounded-2xl hover:bg-red-50 transition-colors font-semibold text-lg shadow-lg hover:shadow-xl"
            >
              <SafeIcon icon={FiMoon} className="text-xl" />
              <span>Not Tonight</span>
            </button>
          </div>

          {/* Today's Log Status and Undo Button */}
          {todayLog && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-xl border-l-4 border-purple-500">
                <p className="text-lg font-semibold text-gray-800 mb-2">
                  Today's result: {todayLog.good ? '✅ Good Night' : '❌ Not Tonight'}
                </p>
                {todayLog.note && (
                  <p className="text-gray-600">
                    <strong>Note:</strong> {todayLog.note}
                  </p>
                )}
              </div>
              
              {onUndoLog && (
                <button
                  onClick={onUndoLog}
                  className="inline-flex items-center space-x-2 px-6 py-3 rounded-lg bg-gray-500 text-white hover:bg-gray-600 transition-colors font-medium"
                >
                  <SafeIcon icon={FiRotateCcw} />
                  <span>Undo Today's Log</span>
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Calendar */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-800 flex items-center space-x-2">
            <span>📅</span>
            <span>Sleep Calendar</span>
          </h2>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <SafeIcon icon={FiChevronLeft} className="text-gray-600" />
            </button>
            <span className="text-lg font-semibold text-gray-700 min-w-[160px] text-center">
              {format(currentMonth, 'MMMM yyyy')}
            </span>
            <button
              onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <SafeIcon icon={FiChevronRight} className="text-gray-600" />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-7 gap-1 mb-6">
          {/* Day headers */}
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="text-center text-sm font-semibold text-gray-600 py-3">
              {day}
            </div>
          ))}
          
          {/* Empty cells for days before month start */}
          {Array.from({ length: monthStart.getDay() }).map((_, index) => (
            <div key={index} className="w-12 h-12"></div>
          ))}
          
          {/* Month days */}
          {monthDays.map(date => (
            <div
              key={date.toString()}
              className={getDayClassName(date)}
              onClick={() => handleDayClick(date)}
            >
              {getDayContent(date)}
            </div>
          ))}
        </div>

        <div className="flex items-center justify-center space-x-8 text-sm text-gray-600">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
              <SafeIcon icon={FiCheck} className="text-white text-xs" />
            </div>
            <span>Good Night</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
              <SafeIcon icon={FiX} className="text-white text-xs" />
            </div>
            <span>Not Tonight</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 border-2 border-blue-500 rounded-full bg-white"></div>
            <span>Today</span>
          </div>
        </div>
      </div>

      {/* Calendar Modal - Fixed positioning to top */}
      <AnimatePresence>
        {showModal && selectedDate && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex justify-center p-4 z-50 overflow-y-auto"
            style={{ alignItems: 'flex-start', paddingTop: '2rem' }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: -20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: -20 }}
              className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl my-4"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-800">
                  Sleep Log - {format(selectedDate, 'EEEE, MMMM d')}
                </h3>
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={handleModalClose}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <SafeIcon icon={FiX} className="text-gray-500" />
                </motion.button>
              </div>

              <div className="text-center mb-6">
                <motion.div 
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2 }}
                  className="text-4xl mb-2"
                >
                  {child?.avatarEmoji}
                </motion.div>
                <p className="text-gray-600">How was {child?.name}'s bedtime?</p>
              </div>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="space-y-4"
              >
                <div className="flex space-x-3">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleModalSubmit(true)}
                    className="flex-1 flex items-center justify-center space-x-2 bg-green-600 text-white py-3 px-4 rounded-xl hover:bg-green-700 transition-colors shadow-lg hover:shadow-xl font-semibold"
                  >
                    <SafeIcon icon={FiSun} />
                    <span>Good Night</span>
                  </motion.button>
                  
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleModalSubmit(false)}
                    className="flex-1 flex items-center justify-center space-x-2 bg-red-600 text-white py-3 px-4 rounded-xl hover:bg-red-700 transition-colors shadow-lg hover:shadow-xl font-semibold"
                  >
                    <SafeIcon icon={FiMoon} />
                    <span>Not Tonight</span>
                  </motion.button>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Add a note about bedtime (optional)
                  </label>
                  <textarea
                    value={modalNote}
                    onChange={(e) => setModalNote(e.target.value.slice(0, 140))}
                    placeholder="e.g., Had trouble falling asleep tonight..."
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none transition-all duration-200"
                    rows="3"
                  />
                  <div className="flex justify-between items-center mt-1">
                    <p className="text-xs text-gray-500">{modalNote.length}/140 characters</p>
                    {modalNote.length > 100 && (
                      <p className="text-xs text-amber-600">Getting close to limit!</p>
                    )}
                  </div>
                </div>

                <div className="pt-2">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleModalClose}
                    className="w-full bg-gray-200 text-gray-700 py-3 px-4 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                  >
                    Cancel
                  </motion.button>
                </div>
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default SleepCalendar;